import * as yup from 'yup';
import { sourcingNewsItemSchema } from './SourcingListItems.schema';

// ─── raw API schema ───────────────────────────────────────────────────────────

export const agentCompanyResultSchema = yup.object({
  fabric_entity_id: yup.string().required(),
  name: yup.string().required(),
  legal_name: yup.string().nullable().default(null),
  domain: yup.string().nullable().default(null),
  homepage_url: yup.string().nullable().default(null),
  linkedin_url: yup.string().nullable().default(null),
  logo_url: yup.string().nullable().default(null),
  short_description: yup.string().nullable().default(null),
  country_code: yup.string().nullable().default(null),
  state_code: yup.string().nullable().default(null),
  city: yup.string().nullable().default(null),
  postal_code: yup.string().nullable().default(null),
  full_address: yup.string().nullable().default(null),
  employee_count: yup.number().nullable().default(null),
  // Can be a year integer (2018) or an ISO date string ("2020-01-01T00:00:00+00:00")
  founded_year: yup.mixed<number | string>().nullable().default(null),
  total_funding_usd: yup.number().nullable().default(null),
  last_funding_date: yup.string().nullable().default(null),
  last_funding_amount: yup.number().nullable().default(null),
  last_funding_type: yup.string().nullable().default(null),
  num_funding_rounds: yup.number().nullable().default(null),
  funding_stage: yup.string().nullable().default(null),
  industry: yup.string().nullable().default(null),
  keywords: yup.array(yup.string().required()).nullable().default(null),
  company_type: yup.string().nullable().default(null),
  status: yup.string().nullable().default(null),
  ownership_status: yup.string().nullable().default(null),
  twitter_url: yup.string().nullable().default(null),
  facebook_url: yup.string().nullable().default(null),
  phone: yup.string().nullable().default(null),
  email: yup.string().nullable().default(null),
  valuation: yup.number().nullable().default(null),
  customer_type: yup.string().nullable().default(null),
  tags: yup.array(yup.string().required()).nullable().default(null),
  sector: yup.string().nullable().default(null),
  ceo_name: yup.string().nullable().default(null),
  founders: yup.string().nullable().default(null),
  key_executives: yup.string().nullable().default(null),
  investors: yup.string().nullable().default(null),
  num_investors: yup.number().nullable().default(null),
  last_round_investors: yup.string().nullable().default(null),
  source_crunchbase: yup.boolean().nullable().default(null),
  source_harmonic: yup.boolean().nullable().default(null),
  source_pitchbook: yup.boolean().nullable().default(null),
  source_coverage: yup.number().nullable().default(null),
  _relevance_score: yup.number().nullable().default(null),
});

export type IAgentCompanyResult = yup.InferType<typeof agentCompanyResultSchema>;

export const agentSearchResponseSchema = yup.object({
  data: yup.array(agentCompanyResultSchema.required()).required(),
  total: yup.number().required(),
  page: yup.number().required(),
  page_size: yup.number().required(),
  total_pages: yup.number().required(),
});

export type IAgentSearchResponse = yup.InferType<typeof agentSearchResponseSchema>;

// ─── helpers ─────────────────────────────────────────────────────────────────

function parseFoundedYear(value: number | string | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  const year = new Date(value).getUTCFullYear();
  return isNaN(year) ? null : year;
}

function splitCsv(value: string | null | undefined): string[] | null {
  if (!value) {
    return null;
  }
  const parts = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : null;
}

function buildLocation(orig: IAgentCompanyResult): string | null {
  if (orig.full_address) {
    return orig.full_address;
  }
  const parts = [orig.city, orig.state_code, orig.country_code].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

// ─── mapping schema ───────────────────────────────────────────────────────────

export const agentSourcingCompanyRowSchema = yup
  .object({
    id: yup.string().required(),
    listId: yup.string().required(),
    enrichmentId: yup.string().nullable().default(null),
    acquired: yup.boolean().required(),
    acquiredBy: yup.string().nullable().default(null),
    aliases: yup.array(yup.string().required()).nullable().default(null),
    allInvestors: yup.array(yup.string().required()).nullable().default(null),
    annualRevenue: yup.number().nullable().default(null),
    ceoName: yup.string().nullable().default(null),
    crunchbaseUrl: yup.string().nullable().default(null),
    domain: yup.string().nullable().default(null),
    emails: yup.array(yup.string().required()).nullable().default(null),
    facebookUrl: yup.string().nullable().default(null),
    foundedYear: yup.number().nullable().default(null),
    founders: yup.array(yup.string().required()).nullable().default(null),
    headcount: yup.string().nullable().default(null),
    industry: yup.string().nullable().default(null),
    lastEnrichmentRequestId: yup.string().nullable().default(null),
    lastFundingRaised: yup.number().nullable().default(null),
    latestFundingStage: yup.string().nullable().default(null),
    legalName: yup.string().nullable().default(null),
    linkedinUrl: yup.string().nullable().default(null),
    logoUrl: yup.string().nullable().default(null),
    longDescription: yup.string().nullable().default(null),
    name: yup.string().required(),
    numFundingRounds: yup.number().nullable().default(null),
    phones: yup.array(yup.string().required()).nullable().default(null),
    pitchbookUrl: yup.string().nullable().default(null),
    postValuation: yup.number().nullable().default(null),
    primaryLocation: yup.string().nullable().default(null),
    recentNews: yup.array(sourcingNewsItemSchema.required()).nullable().default(null),
    secondaryLocation: yup.string().nullable().default(null),
    sector: yup.string().nullable().default(null),
    shortDescription: yup.string().nullable().default(null),
    totalFunding: yup.number().nullable().default(null),
    twitterUrl: yup.string().nullable().default(null),
    website: yup.string().nullable().default(null),
  })
  .transform((_val, orig: IAgentCompanyResult) => ({
    id: orig.fabric_entity_id,
    listId: '',
    enrichmentId: null,
    acquired: orig.status?.toLowerCase() === 'acquired',
    acquiredBy: null,
    aliases: null,
    allInvestors: splitCsv(orig.investors),
    annualRevenue: null,
    ceoName: orig.ceo_name ?? null,
    crunchbaseUrl: null,
    domain: orig.domain ?? null,
    emails: orig.email ? [orig.email] : null,
    facebookUrl: orig.facebook_url ?? null,
    foundedYear: parseFoundedYear(orig.founded_year),
    founders: splitCsv(orig.founders),
    headcount: orig.employee_count != null ? String(orig.employee_count) : null,
    industry: orig.industry ?? null,
    lastEnrichmentRequestId: null,
    lastFundingRaised: orig.last_funding_amount ?? null,
    latestFundingStage: orig.funding_stage ?? null,
    legalName: orig.legal_name ?? null,
    linkedinUrl: orig.linkedin_url ?? null,
    logoUrl: orig.logo_url ?? null,
    longDescription: null,
    name: orig.name,
    numFundingRounds: orig.num_funding_rounds ?? null,
    phones: orig.phone ? [orig.phone] : null,
    pitchbookUrl: null,
    postValuation: orig.valuation ?? null,
    primaryLocation: buildLocation(orig),
    recentNews: null,
    secondaryLocation: null,
    sector: orig.sector ?? null,
    shortDescription: orig.short_description ?? null,
    totalFunding: orig.total_funding_usd ?? null,
    twitterUrl: orig.twitter_url ?? null,
    website: orig.homepage_url ?? null,
  }));
